import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { amenitiesApi, type AmenityDeviceOut } from '@/api/amenities';
import { Card } from '@/components/ui/Card';
import { StatusPill } from '@/components/ui/StatusPill';

/**
 * Card "Aspiração" exibido no detalhe da estação quando `capabilities.vacuum`
 * está ligada. Busca os devices da estação; se nenhum habilitado, não renderiza
 * nada. Toca → tela de oferta (`/amenities/:stationId/vacuum`).
 */
export function VacuumCard({ stationId }: { stationId: string }) {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<AmenityDeviceOut[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    amenitiesApi
      .devices(stationId)
      .then((d) => {
        if (!cancelled) setDevices(d);
      })
      .catch(() => {
        if (!cancelled) setDevices([]);
      });
    return () => {
      cancelled = true;
    };
  }, [stationId]);

  if (!devices || devices.length === 0) return null;

  const anyAvailable = devices.some((d) => d.is_available);
  const allBlocks = devices.flatMap((d) => d.blocks);
  const minPrice = Math.min(...allBlocks.map((b) => Number(b.price_brl)));
  const minMinutes = Math.round(
    Math.min(...allBlocks.map((b) => b.seconds)) / 60
  );

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={() => navigate(`/amenities/${stationId}/vacuum?origin=station`)}
        className="text-left"
      >
        <Card className="flex items-center gap-3 p-4 transition hover:shadow-mid">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-bow-royal/5 text-bow-royal">
            <BroomGlyph size={22} />
          </div>
          <div className="flex flex-1 flex-col gap-0.5">
            <span
              className="leading-tight text-bow-royal"
              style={{
                fontFamily: 'Chillax, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 15
              }}
            >
              Aspiração
            </span>
            <span className="text-xs text-bow-royal/60">
              Limpe seu carro enquanto recarrega · a partir de R${' '}
              {minPrice.toFixed(2).replace('.', ',')} / {minMinutes} min
            </span>
            <div className="mt-1.5">
              <StatusPill tone={anyAvailable ? 'green' : 'muted'}>
                {anyAvailable ? 'Disponível' : 'Em uso'}
              </StatusPill>
            </div>
          </div>
          <ChevronRight />
        </Card>
      </button>
    </div>
  );
}

function BroomGlyph({ size = 22 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
      <path d="M19.36 2.72 21 4.36l-3.34 3.34 1.7 1.7-1.42 1.42-1.7-1.7-3.5 3.5c1.5 1.86 1.4 4.6-.32 6.32l-.7.7-7.78-7.78.7-.7c1.72-1.72 4.46-1.82 6.32-.32l3.5-3.5-1.7-1.7L14.18 7l1.7 1.7 3.48-5.98zM6.4 13.32l4.28 4.28c.78-.94.73-2.35-.16-3.24l-.88-.88c-.9-.9-2.3-.94-3.24-.16z" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-bow-royal/40"
      aria-hidden
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}
